import { useContext, useState } from 'react'
import { useFirestoreGeneral, useFirestoreGeneralOrderBy } from '../../../firebase/useFirestore'
import { db } from '../../../firebase/config'
import { SavedIcon } from '../../../StateManagment/SavedIcon'
import ReadOnlyGuard from '../../common/ReadOnlyGuard'
import readHouseholdField, { HOUSEHOLD_FIELDS, formatHouseholdField } from '../../../helpers/adressHelpers/householdFields'
import buildFixedFieldPatch from '../../../helpers/adressHelpers/buildFixedFieldPatch'

/**
 * De kaart 'Vaste gegevens' op het tabblad Gegevens van een huishouden (en in
 * de afspraak): naam, contact, adres en woning, met een vaste naam en vorm voor
 * elke organisatie. Nodig omdat de gegevenstabel daarvoor niet deugt: daar is
 * elke titel vrije tekst, en de code die op die titels leest ging stil stuk.
 * AI-CONTEXT: See docs/features/huishouden-vaste-gegevens.md (T4)
 *
 * Opslaan per veld, op het huishouden zelf (`Adresses`). Een tekstveld bij het
 * verlaten, een keuze meteen — geen opslaanknop, zoals de rest van dit tabblad.
 * Een waarde die nog uit een oude gegevensregel komt en die je ongewijzigd laat,
 * wordt NIET overgezet: dat doet de migratie, met een rapport vooraf.
 */

/** @typedef {import('../../../helpers/adressHelpers/householdFields').HouseholdFieldDef} HouseholdFieldDef */

const GROUPS = [
    { key: 'contact', title: 'Adres en contact' },
    { key: 'woning', title: 'Woning' },
]

const fieldsOf = (group) => Object.entries(HOUSEHOLD_FIELDS).filter(([, def]) => def.group === group)

/**
 * Eén vrij in te vullen veld. Ongestuurd, met de opgeslagen waarde als sleutel:
 * verandert die in de database, dan volgt het veld; typ je in een ánder veld,
 * dan blijft dit veld met rust.
 * @param {object} props
 * @param {string} props.field - Sleutel uit HOUSEHOLD_FIELDS
 * @param {HouseholdFieldDef} props.def
 * @param {string|number|null} props.value - Wat er nu staat
 * @param {(field: string, raw: string) => void} props.onSave - Bij het verlaten van het veld
 */
const FixedTextField = ({ field, def, value, onSave }) => (
    <input
        key={String(value ?? '')}
        id={`fixed-field-${field}`}
        type="text"
        inputMode={def.kind === 'number' ? 'numeric' : undefined}
        defaultValue={value ?? ''}
        autoComplete="off"
        onBlur={(e) => onSave(field, e.target.value)}
    />
)

/**
 * Eigendom als drie knoppen; een tweede klik op de gekozen knop zet hem leeg.
 * @param {object} props
 * @param {string} props.field
 * @param {HouseholdFieldDef} props.def
 * @param {string} props.value
 * @param {(field: string, raw: string) => void} props.onSave
 */
const FixedChoiceButtons = ({ field, def, value, onSave }) => (
    <div className="fixed-data-choices" role="group" aria-labelledby={`fixed-field-${field}-label`}>
        {def.options.map((option) => (
            <button
                key={option.value}
                type="button"
                aria-pressed={value === option.value}
                className={value === option.value ? 'fixed-data-choice fixed-data-choice--on' : 'fixed-data-choice'}
                onClick={() => onSave(field, value === option.value ? '' : option.value)}
            >
                {option.label}
            </button>
        ))}
    </div>
)

/**
 * @param {object} props
 * @param {string} props.field
 * @param {HouseholdFieldDef} props.def
 * @param {string} props.value
 * @param {(field: string, raw: string) => void} props.onSave
 */
const FixedSelect = ({ field, def, value, onSave }) => (
    <select id={`fixed-field-${field}`} value={value || ''} onChange={(e) => onSave(field, e.target.value)}>
        <option value="">Kies…</option>
        {def.options.map((option) => (
            <option key={option.value} value={option.value}>{option.label}</option>
        ))}
    </select>
)

/**
 * @param {object} props
 * @param {string} props.adressID - Adresses.ID van het huishouden (niet de docid)
 */
const HouseholdFixedDataCard = ({ adressID }) => {
    const [, setSaved] = useContext(SavedIcon)
    /** Melding per veld: ongeldige invoer of een mislukte opslag. */
    const [messages, setMessages] = useState(/** @type {Record<string, string>} */ ({}))

    const adressList = useFirestoreGeneral('Adresses', 'ID', adressID || '')
    const rows = useFirestoreGeneralOrderBy('PersonaData', 'PersonaID', adressID || '', 'Position', 'asc')

    const adress = Array.isArray(adressList) && adressList.length > 0 ? adressList[0] : null
    if (!adress) return null

    const personaRows = Array.isArray(rows) ? rows : undefined
    const valueOf = (field) => readHouseholdField(adress, personaRows, field)
    const setMessage = (field, text) => setMessages((prev) => ({ ...prev, [field]: text }))

    /**
     * @param {string} field
     * @param {string} raw
     */
    const save = async (field, raw) => {
        const result = buildFixedFieldPatch(field, raw)
        if (result.error) {
            setMessage(field, result.error)
            return
        }
        setMessage(field, '')
        if (result.patch[field] === (valueOf(field) ?? (HOUSEHOLD_FIELDS[field].kind === 'number' ? null : ''))) return

        try {
            await db.collection('Adresses').doc(adress.docid).update(result.patch)
            setSaved('flex')
        } catch (err) {
            console.error(`Opslaan van ${field} mislukt:`, err)
            setMessage(field, `${HOUSEHOLD_FIELDS[field].label} kon niet worden opgeslagen. Probeer het opnieuw.`)
        }
    }

    const renderControl = (field, def) => {
        const value = valueOf(field)
        if (field === 'Eigendom') return <FixedChoiceButtons field={field} def={def} value={value} onSave={save} />
        if (def.kind === 'enum') return <FixedSelect field={field} def={def} value={value} onSave={save} />
        return <FixedTextField field={field} def={def} value={value} onSave={save} />
    }

    return (
        <div className="dashboard-container adress-household-fixed-data">
            <h3>Vaste gegevens</h3>
            <ReadOnlyGuard edit="addresses.edit">
                {(canEdit) => (
                    <div className="fixed-data-groups">
                        {GROUPS.map((group) => (
                            <div key={group.key} className="fixed-data-group">
                                <h4>{group.title}</h4>
                                {fieldsOf(group.key).map(([field, def]) => (
                                    <div key={field} className="fixed-data-field">
                                        <label id={`fixed-field-${field}-label`} htmlFor={`fixed-field-${field}`}>{def.label}</label>
                                        {canEdit ? renderControl(field, def) : <span>{formatHouseholdField(field, valueOf(field)) || '—'}</span>}
                                        {messages[field] && <p className="fixed-data-message" role="alert">{messages[field]}</p>}
                                    </div>
                                ))}
                            </div>
                        ))}
                    </div>
                )}
            </ReadOnlyGuard>
        </div>
    )
}

export default HouseholdFixedDataCard
